import { useEffect, useRef, useState } from "react";
import { Pencil, Plus, Trash2, Upload, ImageIcon, RefreshCw, PackageX, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { useProducts } from "../../context/ProductsContext";
import { updateProduct, uploadProductImage } from "../../lib/api";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/*
  Admin → Products tab.
  Table of every product from D1. "Edit" opens a modal; saving sends PUT /api/products/:id
  (plus POST /api/products/:id/image for a new photo). On 200 the saved product replaces the
  one in global state, so the public menu shows the change immediately.
*/
export default function ProductsAdmin({ onUnauthorized }) {
  const { products, source, refresh, replaceProduct } = useProducts();
  const [editing, setEditing] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Always start from what's in the database right now.
  useEffect(() => {
    refresh();
  }, [refresh]);

  const reload = async () => {
    setRefreshing(true);
    await refresh();
    setRefreshing(false);
  };

  if (source === "fallback") {
    return (
      <div data-testid="admin-products-offline" className="bg-white rounded-2xl border border-[#EFE9E0] p-6">
        <p className="font-serif text-lg text-ink">Product database not reachable</p>
        <p className="mt-2 text-sm text-[#6E6760]">
          The menu is showing its built-in items. Check that the D1 database is bound as <code>DB</code> on the
          Worker, then refresh.
        </p>
        <Button onClick={reload} variant="outline" className="mt-4 rounded-full">
          <RefreshCw size={16} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Try again
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="admin-products">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-[#6E6760]">
          Changes save straight to the database and show on the menu right away.
        </p>
        <Button variant="outline" onClick={reload} className="rounded-full border-[#E8E2D9] bg-transparent h-9">
          <RefreshCw size={15} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Reload
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-[#EFE9E0] overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-products-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-[#787069] border-b border-[#EFE9E0]">
              <th className="px-4 py-3 font-semibold">Product</th>
              <th className="px-4 py-3 font-semibold">Sizes &amp; prices</th>
              <th className="px-4 py-3 font-semibold">In stock</th>
              <th className="px-4 py-3 font-semibold hidden md:table-cell">Updated</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} data-testid={`admin-product-row-${p.slug || p.id}`} className="border-b border-[#F3EEE7] last:border-0 align-top">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 min-w-[220px]">
                    {p.image_url ? (
                      <img src={p.image_url} alt="" className="w-12 h-12 rounded-lg object-cover bg-[#F5F0EA] shrink-0" />
                    ) : (
                      <span className="w-12 h-12 rounded-lg bg-[#F5F0EA] grid place-items-center text-[#A89F94] shrink-0">
                        <ImageIcon size={18} />
                      </span>
                    )}
                    <div>
                      <p className="font-medium text-ink">{p.name}</p>
                      <p className="text-xs text-[#787069] line-clamp-1 max-w-[260px]">{p.description}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5 min-w-[160px]">
                    {(p.sizes || []).map((s) => (
                      <span key={s.size} className="px-2 py-0.5 rounded-md bg-[#F5F0EA] text-[#524B46] whitespace-nowrap">
                        {s.size} · {money(s.price)}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <StockBadge quantity={p.quantity} />
                </td>
                <td className="px-4 py-3 text-xs text-[#9a938c] hidden md:table-cell whitespace-nowrap">
                  {p.updated_at ? new Date(p.updated_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    data-testid={`admin-edit-${p.slug || p.id}`}
                    onClick={() => setEditing(p)}
                    className="h-9 rounded-full bg-olive hover:bg-olive-hover text-cream text-xs px-4"
                  >
                    <Pencil size={14} className="mr-1.5" /> Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditProductModal
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            replaceProduct(saved);
            setEditing(null);
            toast.success(`"${saved.name}" saved — the menu is updated.`);
          }}
          onUnauthorized={onUnauthorized}
        />
      )}
    </div>
  );
}

const StockBadge = ({ quantity }) => {
  if (quantity === null || quantity === undefined) {
    return <span className="text-xs text-[#787069]">Not tracked</span>;
  }
  if (quantity === 0) {
    return <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#FBEAEA] text-[#9B2C2C]">Sold out</span>;
  }
  const low = quantity <= 5;
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${low ? "bg-[#FDF5F0] text-[#A75429]" : "bg-[#F3F7F1] text-[#3E5334]"}`}>
      {quantity} left
    </span>
  );
};

// Shrinks a photo in the browser (longest side ≤ 1200px, JPEG) before upload.
async function shrinkImage(file, maxSide = 1200, quality = 0.85) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff"; // PNG transparency → white, not black
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not read that image"))), "image/jpeg", quality)
  );
}

function EditProductModal({ product, onClose, onSaved, onUnauthorized }) {
  const [name, setName] = useState(product.name || "");
  const [description, setDescription] = useState(product.description || "");
  const [imageUrl, setImageUrl] = useState(product.image_url || "");
  const [sizes, setSizes] = useState(
    (product.sizes && product.sizes.length ? product.sizes : [{ size: "Regular", price: product.price || 0 }]).map((s) => ({
      size: s.size,
      price: String(s.price),
    }))
  );
  const [trackStock, setTrackStock] = useState(product.quantity !== null && product.quantity !== undefined);
  const [quantity, setQuantity] = useState(product.quantity ?? 0);
  const [photo, setPhoto] = useState(null); // { blob, preview }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => () => photo && URL.revokeObjectURL(photo.preview), [photo]);

  const setSize = (i, key, val) => setSizes((cur) => cur.map((s, j) => (j === i ? { ...s, [key]: val } : s)));

  const pickPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      setError("Choose a JPEG, PNG or WebP image.");
      return;
    }
    try {
      const blob = await shrinkImage(file);
      if (blob.size > 1_500_000) throw new Error("That image is still over 1.5 MB after resizing.");
      setPhoto({ blob, preview: URL.createObjectURL(blob) });
      setError("");
    } catch (err) {
      setError(err.message || "Could not read that image.");
    }
  };

  const validate = () => {
    if (!name.trim()) return "Give the product a title.";
    if (sizes.length === 0) return "Add at least one size and price.";
    for (const s of sizes) {
      if (!s.size.trim()) return "Every size needs a label.";
      const n = Number(s.price);
      if (!Number.isFinite(n) || n <= 0) return `Enter a price above $0 for "${s.size || "this size"}".`;
    }
    const labels = sizes.map((s) => s.size.trim().toLowerCase());
    if (new Set(labels).size !== labels.length) return "Two sizes have the same label.";
    if (trackStock && (!Number.isInteger(Number(quantity)) || Number(quantity) < 0)) return "Stock must be a whole number, 0 or more.";
    if (!photo && imageUrl && !/^(https:\/\/|\/)/.test(imageUrl.trim())) return "Image URL must start with https:// or /.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (photo) await uploadProductImage(product.id, photo.blob);
      const patch = {
        name: name.trim(),
        description: description.trim(),
        sizes: sizes.map((s) => ({ size: s.size.trim(), price: Math.round(Number(s.price) * 100) / 100 })),
        quantity: trackStock ? Number(quantity) : null,
      };
      if (!photo) patch.image_url = imageUrl.trim();
      const saved = await updateProduct(product.id, patch); // resolves only on 200 OK
      onSaved(saved);
    } catch (err) {
      if (err.message === "unauthorized") {
        onClose();
        onUnauthorized && onUnauthorized();
      } else {
        setError(err.message || "Could not save. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const preview = photo ? photo.preview : imageUrl;

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto bg-white" data-testid="admin-edit-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ink">Edit product</DialogTitle>
          <DialogDescription>Saved changes appear on the public menu straight away.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label htmlFor="p-name">Title</Label>
            <Input id="p-name" data-testid="edit-name" value={name} maxLength={120}
              onChange={(e) => setName(e.target.value)} className="mt-2 h-11 bg-cream" />
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <Label htmlFor="p-desc">Description</Label>
              <span className="text-xs text-[#9a938c]">{description.length}/1000</span>
            </div>
            <Textarea id="p-desc" data-testid="edit-description" value={description} maxLength={1000} rows={3}
              onChange={(e) => setDescription(e.target.value)} className="mt-2 bg-cream" />
          </div>

          <div>
            <Label>Photo</Label>
            <div className="mt-2 flex flex-col sm:flex-row gap-4">
              <div className="w-full sm:w-40 aspect-[4/3] rounded-xl overflow-hidden bg-[#F5F0EA] grid place-items-center shrink-0">
                {preview ? (
                  <img src={preview} alt="Preview" className="w-full h-full object-cover" data-testid="edit-image-preview" />
                ) : (
                  <ImageIcon className="text-[#A89F94]" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pickPhoto} />
                <Button type="button" variant="outline" onClick={() => fileRef.current && fileRef.current.click()}
                  data-testid="edit-upload" className="rounded-full h-9 border-[#E8E2D9]">
                  <Upload size={15} className="mr-1.5" /> {photo ? "Choose a different photo" : "Upload a photo"}
                </Button>
                {photo ? (
                  <p className="text-xs text-[#6E6760]">
                    New photo ready ({Math.round(photo.blob.size / 1024)} KB) — it uploads when you save.{" "}
                    <button type="button" className="text-olive underline" onClick={() => setPhoto(null)}>Undo</button>
                  </p>
                ) : (
                  <div className="relative">
                    <Link2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A89F94]" />
                    <Input data-testid="edit-image-url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)}
                      placeholder="…or paste an image URL (https://…)" className="h-10 pl-8 bg-cream text-xs" />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <Label>Sizes &amp; prices</Label>
            <div className="mt-2 space-y-2" data-testid="edit-sizes">
              {sizes.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input aria-label="Size label" data-testid={`edit-size-${i}`} value={s.size} maxLength={40}
                    onChange={(e) => setSize(i, "size", e.target.value)} placeholder="e.g. Small, 6-pack"
                    className="h-10 bg-cream flex-1" />
                  <div className="relative w-32">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#787069]">$</span>
                    <Input aria-label="Price" data-testid={`edit-price-${i}`} type="number" inputMode="decimal" min="0.01"
                      step="0.01" value={s.price} onChange={(e) => setSize(i, "price", e.target.value)}
                      className="h-10 pl-6 bg-cream" />
                  </div>
                  <Button type="button" variant="ghost" aria-label="Remove size" disabled={sizes.length === 1}
                    onClick={() => setSizes((cur) => cur.filter((_, j) => j !== i))} className="h-10 w-10 p-0 text-[#9B2C2C]">
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))}
            </div>
            {sizes.length < 10 && (
              <button type="button" onClick={() => setSizes((cur) => [...cur, { size: "", price: "" }])}
                className="mt-2 inline-flex items-center gap-1 text-sm text-olive hover:underline">
                <Plus size={14} /> Add a size
              </button>
            )}
          </div>

          <div className="rounded-xl border border-[#EFE9E0] p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label htmlFor="p-track">Track stock</Label>
                <p className="text-xs text-[#787069] mt-0.5">
                  Off = always available. On = the menu shows "Only N left" / "Sold out" and checkout won't oversell.
                </p>
              </div>
              <Switch id="p-track" data-testid="edit-track-stock" checked={trackStock} onCheckedChange={setTrackStock} />
            </div>
            {trackStock && (
              <div className="mt-3 flex items-center gap-3">
                <Label htmlFor="p-qty" className="whitespace-nowrap">Quantity in stock</Label>
                <Input id="p-qty" data-testid="edit-quantity" type="number" min="0" step="1" value={quantity}
                  onChange={(e) => setQuantity(e.target.value)} className="h-10 w-28 bg-cream" />
                {Number(quantity) === 0 && (
                  <span className="inline-flex items-center gap-1 text-xs text-[#9B2C2C]"><PackageX size={14} /> Shows as sold out</span>
                )}
              </div>
            )}
          </div>

          {error && (
            <p data-testid="edit-error" className="rounded-xl bg-[#FDF5F0] border border-[#F0D9CB] px-4 py-3 text-sm text-[#8A5A3B]">
              {error}
            </p>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} className="rounded-full">
              Cancel
            </Button>
            <Button type="submit" data-testid="edit-save" disabled={saving} className="rounded-full bg-olive hover:bg-olive-hover text-cream">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
